class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    this.watchDebugChanges();
  }

  // Temporary: logs the real caller (synchronous stack) of anything that marks
  // the drawer empty or rewrites its markup.
  watchDebugChanges() {
    const drawer = this;
    const classList = this.classList;
    ['add', 'remove', 'toggle'].forEach((method) => {
      const original = classList[method].bind(classList);
      classList[method] = (...args) => {
        const wasEmpty = classList.contains('is-empty');
        const result = original(...args);
        const isEmpty = classList.contains('is-empty');
        if (wasEmpty !== isEmpty) {
          console.groupCollapsed(`[cart debug] is-empty ${isEmpty ? 'ADDED' : 'removed'} on cart-drawer`);
          console.log(new Error('caller').stack);
          console.groupEnd();
          cartDebug('is-empty changed');
        }
        return result;
      };
    });

    const innerHTML = Object.getOwnPropertyDescriptor(Element.prototype, 'innerHTML');
    Object.defineProperty(Element.prototype, 'innerHTML', {
      configurable: true,
      get() {
        return innerHTML.get.call(this);
      },
      set(value) {
        if (drawer.contains(this) && this.matches('#CartDrawer, .drawer__inner, #CartDrawer-CartItems')) {
          console.groupCollapsed(`[cart debug] innerHTML replaced on ${this.id ? '#' + this.id : this.className}`);
          console.log({
            newLength: String(value).length,
            newLineItems: (String(value).match(/class="cart-item"/g) || []).length,
            hasEmptyBlock: String(value).includes('drawer__inner-empty'),
            hasHeader: String(value).includes('drawer__header'),
            hasFooter: String(value).includes('drawer__footer'),
          });
          console.log(new Error('caller').stack);
          console.groupEnd();
        }
        innerHTML.set.call(this, value);
      },
    });

    const replaceWith = Element.prototype.replaceWith;
    Element.prototype.replaceWith = function (...nodes) {
      if (drawer.contains(this) && this.matches('cart-drawer-items, .cart-drawer__footer, .drawer__footer')) {
        console.groupCollapsed(`[cart debug] replaceWith on ${this.tagName.toLowerCase()}.${this.className}`);
        console.log(new Error('caller').stack);
        console.groupEnd();
      }
      return replaceWith.apply(this, nodes);
    };
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    // The slide starts at translateX(100%). Replacing the drawer markup in the
    // same turn as .active leaves that panel off-screen, so pin it open.
    this.classList.add('animate', 'active');
    const inner = this.querySelector('.drawer__inner');
    if (inner) inner.style.setProperty('transform', 'translateX(0)', 'important');

    const containerToTrapFocusOn = this.classList.contains('is-empty')
      ? this.querySelector('.drawer__inner-empty')
      : document.getElementById('CartDrawer');
    const focusElement = this.querySelector('.drawer__close') || inner;
    if (containerToTrapFocusOn) trapFocus(containerToTrapFocusOn, focusElement);

    document.body.classList.add('overflow-hidden');
    cartDebug('drawer: open');
  }

  close() {
    cartDebug('drawer: close');
    const inner = this.querySelector('.drawer__inner');
    if (inner) inner.style.removeProperty('transform');
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.querySelectorAll('product-form').forEach((form) => form.resetSubmitButton?.());
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;
    this.rendering = true;

    const drawerHtml = parsedState.sections?.['cart-drawer'];
    cartDebug('drawer: renderContents', {
      sectionKeys: Object.keys(parsedState.sections || {}),
      drawerHtmlLength: drawerHtml?.length,
    });
    if (!drawerHtml) {
      this.rendering = false;
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const inner = this.getSectionInnerHTML(responseText, '#CartDrawer');
          const sectionElement = document.getElementById('CartDrawer');
          if (sectionElement && inner) sectionElement.innerHTML = inner;
          this.classList.toggle('is-empty', !this.querySelector('.cart-item'));
          this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
          this.open();
        })
        .catch((error) => {
          console.error(error);
          this.open();
        });
      return;
    }

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const sectionHtml = parsedState.sections?.[section.id];
      if (!sectionElement || !sectionHtml) return;

      const inner = this.getSectionInnerHTML(sectionHtml, section.selector);
      if (inner) sectionElement.innerHTML = inner;
    });

    this.classList.toggle('is-empty', !this.querySelector('.cart-item'));
    this.rendering = false;
    cartDebug('drawer: renderContents done');

    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.open();
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return '';
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML || '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
